import React from 'react';
import { FiFileText, FiCamera, FiUpload } from 'react-icons/fi';

const FileUploadBox = ({ file, onFileChange, error, accept = 'image/*' }) => {
  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files[0]) {
      onFileChange(e.dataTransfer.files[0]);
    }
  };

  return (
    <div
      onDrop={handleDrop}
      onDragOver={(e) => e.preventDefault()}
      className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
        error ? 'border-red-500 bg-red-900/10' :
        file ? 'border-green-500 bg-green-900/10' :
        'border-gray-700 bg-gray-800 hover:border-gray-600'
      }`}
    >
      <input
        type="file"
        onChange={(e) => onFileChange(e.target.files[0])}
        accept={accept}
        className="hidden"
        id={`file-upload-${Math.random()}`}
      />
      
      <label htmlFor={`file-upload-${Math.random()}`} className="cursor-pointer">
        <div className="flex flex-col items-center">
          <FiUpload className="w-10 h-10 text-gray-400 mb-3" />
          {file ? (
            <>
              <p className="text-green-400 font-medium mb-1">✓ File Selected</p>
              <p className="text-gray-400 text-sm truncate max-w-full">{file.name}</p>
              <p className="text-gray-500 text-xs mt-1">
                {(file.size / 1024 / 1024).toFixed(2)} MB
              </p>
            </>
          ) : (
            <>
              <p className="text-gray-300 font-medium mb-1">Click to upload or drag & drop</p>
              <p className="text-gray-500 text-sm">JPG, PNG, GIF, or PDF (Max 5MB)</p>
            </>
          )}
        </div>
      </label>
      
      {error && (
        <p className="mt-3 text-sm text-red-400">{error}</p>
      )}
    </div>
  );
};

const KycStep2 = ({ formData, files, errors, onInputChange, onFileChange, onPrevStep }) => {
  return (
    <div className="bg-gray-900 rounded-xl p-6">
      <h2 className="text-2xl font-bold text-white mb-2">📸 Document Upload</h2>
      <p className="text-gray-400 mb-6">Upload clear images of your documents</p>
      
      <div className="space-y-8">
        {/* Document Number */}
        <div>
          <label className="block text-gray-300 mb-2">Document Number *</label>
          <input
            type="text"
            value={formData.document_number}
            onChange={(e) => onInputChange('document_number', e.target.value)}
            className={`w-full bg-gray-800 text-white px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.document_number ? 'border-red-500' : 'border-gray-700'
            }`}
            placeholder="Enter your document number"
          />
          {errors.document_number && (
            <p className="mt-2 text-sm text-red-400">{errors.document_number}</p>
          )}
        </div>

        {/* File Uploads Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Document Front */}
          <div>
            <label className="block text-gray-300 mb-3">
              <div className="flex items-center gap-2">
                <FiFileText className="w-5 h-5" />
                Document Front *
              </div>
              <span className="text-xs text-gray-500">Clear image of document front</span>
            </label>
            <FileUploadBox
              file={files.document_front}
              onFileChange={(file) => onFileChange('document_front', file)}
              error={errors.document_front}
            />
          </div>

          {/* Document Back (if not passport) */}
          {formData.document_type !== 'passport' && (
            <div>
              <label className="block text-gray-300 mb-3">
                <div className="flex items-center gap-2">
                  <FiFileText className="w-5 h-5" />
                  Document Back *
                </div>
                <span className="text-xs text-gray-500">Clear image of document back</span>
              </label>
              <FileUploadBox
                file={files.document_back}
                onFileChange={(file) => onFileChange('document_back', file)}
                error={errors.document_back}
              />
            </div>
          )}

          {/* Selfie with Document */}
          <div>
            <label className="block text-gray-300 mb-3">
              <div className="flex items-center gap-2">
                <FiCamera className="w-5 h-5" />
                Selfie with Document *
              </div>
              <span className="text-xs text-gray-500">You holding the document</span>
            </label>
            <FileUploadBox
              file={files.selfie_with_document}
              onFileChange={(file) => onFileChange('selfie_with_document', file)}
              error={errors.selfie_with_document}
            />
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-6 border-t border-gray-800">
          <button
            onClick={onPrevStep}
            className="px-6 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg font-semibold transition-colors"
          >
            Back
          </button>
          <button
            onClick={() => {}} // Handled in parent
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white rounded-lg font-semibold transition-all"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default KycStep2;